import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';


export default function About() {
  return (
    <div className="min-h-screen bg-cream font-sans">
      <Nav />

      {/* ── Page Header ──────────────────────────────────── */}
      <section className="pt-28 lg:pt-36 pb-16 border-b border-warm-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase mb-5">
              The Founder
            </p>
            <h1 className="font-display text-5xl lg:text-[3.5rem] font-bold text-charcoal leading-tight">
              Built on 25 Years of Enterprise Experience.
            </h1>
          </div>
        </div>
      </section>

      {/* ── Story + Photo ─────────────────────────────────── */}
      <section className="py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: copy */}
            <div className="space-y-6">
              <div className="w-10 h-0.5 bg-amber-600" />

              <p className="text-charcoal text-xl leading-relaxed">
                I spent the first chapter of my career in enterprise telecom —
                working at Lucent Technologies and Avaya before moving into
                critical infrastructure engineering at Global Crossing and
                Level 3 Communications.
              </p>

              <p className="text-warm-gray text-lg leading-relaxed">
                From there I spent 11 years as a Principal Sales Consultant at
                Oracle, helping some of the world's largest organizations solve
                complex operational problems with technology.
              </p>

              <p className="text-warm-gray text-lg leading-relaxed">
                After more than two decades at the intersection of technology
                and business, I was ready for something different. I wanted to
                move fast, build things that matter, and work directly with
                business owners — not procurement committees. That's why I
                founded Jambition.
              </p>

              <p className="text-warm-gray text-lg leading-relaxed">
                Today I work with professional services firms — insurance
                agencies, mortgage brokers, staffing firms, wealth management
                practices — helping them build the automation systems that let
                them compete and grow without adding overhead.
              </p>

              <p className="text-warm-gray text-lg leading-relaxed">
                Every project starts with a free AI audit. No pitch, no
                pressure — just a clear picture of where automation can make
                the biggest difference for your business.
              </p>
            </div>

            {/* Right: photo */}
            <div>
              <div className="relative">
                {/* Decorative amber offset */}
                <div className="absolute top-5 left-0 bottom-0 right-5 bg-amber-100" />
                <img
                  src="/headshot.jpg"
                  alt="Josh — Founder, Jambition"
                  className="relative w-full object-cover object-top"
                  style={{ aspectRatio: '3 / 4', maxHeight: '600px' }}
                />
              </div>

              <div className="mt-8 border-t-2 border-warm-border pt-6">
                <p className="font-display text-xl font-bold text-charcoal">
                  Joshua McClain
                </p>
                <p className="text-warm-gray text-sm mt-1">
                  Founder, Jambition
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why This Background Matters ──────────────────── */}
      <section className="py-20 bg-white border-t border-warm-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="w-10 h-0.5 bg-amber-600 mb-8" />
          <h2 className="font-display text-3xl font-bold text-charcoal mb-14">
            Why This Background Matters
          </h2>

          <div className="grid sm:grid-cols-2 gap-0">
            {[
              {
                company: 'Lucent Technologies & Avaya',
                tagline: 'Where I learned how enterprise-grade systems are built from the ground up.',
              },
              {
                company: 'Global Crossing & Level 3 Communications',
                tagline: 'Where I learned to engineer infrastructure that can\'t fail.',
              },
              {
                company: 'Oracle',
                tagline: 'Where I learned how the world\'s largest companies solve complex operational problems with technology.',
              },
              {
                company: 'Jambition',
                tagline: 'Where I apply all of it for businesses that move fast.',
              },
            ].map((item) => (
              <div
                key={item.company}
                className="border-t border-warm-border pl-6 border-l-2 border-l-amber-600 py-8 pr-8"
              >
                <p className="font-display text-xl font-bold text-charcoal mb-2">
                  {item.company}
                </p>
                <p className="text-warm-gray leading-relaxed">{item.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="py-24 bg-amber-600">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl font-bold text-white mb-5">
            Start with a free AI audit.
          </h2>
          <p className="text-amber-100 text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            Let's take a look at your business together. No pitch, no pressure.
          </p>
          <Link
            to="/audit"
            className="inline-flex items-center gap-2 bg-white text-amber-700 hover:bg-amber-50 font-bold px-9 py-4 text-base transition-colors"
          >
            Get Your Free AI Audit <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
